import { useEffect, useState } from 'react';
import type { DateRangePickerRenderIconFunction } from './types';
import DayPicker from 'react-day-picker';

import { useClickOutside } from '../../../hooks';
import { Zindexes } from '../../../theme';
import { LegacyButton } from '../../navigation/LegacyButton';
import { ButtonNav } from '../../navigation/ButtonNav';
import { FloatingBox } from '../../display/FloatingBox';
import { type InputProps } from '@/components/forms/Input';

import { DateRangePickerInput } from './DateRangePickerInput';
import { DateRangePickerNavbar } from './DateRangePickerNavbar';
import { DayModifiers, DateOption, DateRange } from './types';
import { calculateNextRange, isSameDay } from './utils';
import styles from './DateRangePicker.module.scss';
import dayPickerStyles from './DayPicker.module.scss';

import type { RangeModifier } from 'react-day-picker/types/Modifiers';

export type Props = {
  /**
   * Accessibility label that describes this component.
   * This should be supplied if no corresponding `label` element is used.
   */
  'aria-label': string;
  /** Text of close button */
  closeText?: string;
  /** Disabled Dates */
  disabledDates?: DayModifiers;
  /** HTML `id` attribute for the element, used to associate it with a `label`. */
  id?: string;
  /** Disables the input and makes it non-interactive. */
  isDisabled?: boolean;
  /** Indicates the field has an error. */
  isErrored?: boolean;
  /** Alignment of picker menu. */
  menuAlign?: 'left' | 'right' | 'stretch';
  /** ariaLabel of menu component. */
  menuAriaLabel: string;
  /** callback fired when the value of the input changes. */
  onChange: (value: DateRange) => void;
  /** an optional list of predefined DateRanges the user can select from. */
  options?: Array<DateOption>;
  /** A hint to the user of what can be entered in the control. */
  placeholder?: string;
  /** Position of picker menu. */
  menuPosition?: 'south' | 'north';
  /** Controls whether a date range or a single date is selectable. Defaults to 'range'. */
  selectionMode?: 'range' | 'single';
  /** rendered size of menu. */
  size?: 'small' | 'medium' | 'large';
  /** rendered size of the input. */
  inputSize?: InputProps['size'];
  /** value of the input. */
  value?: DateRange;
  /** The variant of the modal (either dropdown or modal). */
  variant?: 'dropdown' | 'modal';
  /** callback fired when date is selected */
  onDayClick?: (day: Date) => void;
  /**
   * Optional custom render for the icon shown before the input value.
   * Return `null` to render nothing. When undefined, a calendar icon is shown.
   */
  renderIconBefore?: DateRangePickerRenderIconFunction;
  /**
   * Optional custom render for the icon shown after the input value.
   * Return `null` to render nothing. When undefined, the clear button is shown when a value exists.
   */
  renderIconAfter?: DateRangePickerRenderIconFunction;
};

const defaultValue = {};

/**
 * A component that allows users to select a date range.
 */
export const DateRangePicker = ({
  'aria-label': ariaLabel,
  menuAriaLabel,
  onChange,
  onDayClick = () => {},
  closeText = 'Done',
  disabledDates = undefined,
  id = undefined,
  isDisabled = false,
  isErrored = false,
  inputSize = 'medium',
  menuAlign = 'left',
  menuPosition = 'south',
  options = [],
  placeholder = undefined,
  selectionMode = 'range',
  size = 'medium',
  value: valueProp = defaultValue,
  variant: variantProp = undefined,
  renderIconAfter,
  renderIconBefore,
}: Props) => {
  const props = {
    'aria-label': ariaLabel,
    menuAriaLabel,
    onChange,
    onDayClick,
    renderIconAfter,
    renderIconBefore,
    value: valueProp,
    placeholder,
    disabledDates,
    id,
    isDisabled,
    isErrored,
    closeText,
    options,
    menuAlign,
    menuPosition,
    size,
    selectionMode,
  } satisfies Props;

  let variant = variantProp;

  if (!variant) {
    if (size === 'small') {
      variant = 'modal';
    } else {
      variant = 'dropdown';
    }
  }

  const [selectedOption, setSelectedOption] = useState<DateOption | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<DateRange>(props.value || defaultValue);
  const { from, to } = value;
  const clampedSize = props.size === 'small' ? props.size : 'medium';
  const isSmall = props.size === 'small';

  useEffect(() => {
    if (props.value !== value) {
      // Prop value was changed so updated internal value to match.
      setValue(props.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const clearSelection = () => {
    setValue(defaultValue);
    props.onChange(defaultValue);
  };

  const closeMenu = () => {
    // Slight hack: Wait until next tick so click events have propagated
    // all the way up the DOM tree. Without this delay, the outside click
    // will call `closeMenu` which will immediately set `isOpen` to `false`.
    // The event will still be propagating and the the event handler that
    // that conditionally calls `openMenu` will be called, and its `isOpen`
    // condition will cause `openMenu` to be called, immediately reopening
    // the menu after it was closed.
    setTimeout(() => {
      setIsOpen(false);
    }, 0);

    // When menu closes fire onChange if the user changed the value.
    if (
      !isSameDay(from, props.value?.from) ||
      !isSameDay(to, props.value?.to)
    ) {
      props.onChange(value);
    }
  };

  const openMenu = () => {
    setIsOpen(true);
  };

  const handleDayClick = (day: Date) => {
    onDayClick(day);
    if (props.selectionMode === 'single') {
      // In single mode, always set the selected day as a single-date range.
      const nextRange = {
        from: day,
        to: day,
      } as DateRange;
      setValue(nextRange);
    } else {
      const nextRange = calculateNextRange(day, from, to);
      setValue(nextRange);
    }
    // Clear any predefined option the user might have selected.
    if (selectedOption !== null) {
      setSelectedOption(null);
    }
  };

  const handleSelectOption = (option: DateOption, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedOption(option);
    setValue(option.value);
  };

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: closeMenu,
  });

  const menuInner = isOpen && (
    <div
      aria-label={props.menuAriaLabel}
      className={styles.menu}
      data-size={props.size}
      data-zds
    >
      {props.options.length > 0 && props.selectionMode !== 'single' && (
        <div className={styles.options} data-size={props.size} data-zds>
          {props.options.map((option) => (
            <ButtonNav
              key={option.key}
              aria-label="Predefined date ranges"
              isButtonFullWidth={true}
              items={[option]}
              onChooseItem={handleSelectOption}
              selectedItemKey={selectedOption?.key}
            />
          ))}
        </div>
      )}
      <DayPicker
        classNames={{
          body: dayPickerStyles.body,
          caption: dayPickerStyles.caption,
          container: dayPickerStyles.container,
          day: dayPickerStyles.day,
          interactionDisabled: dayPickerStyles.interactionDisabled,
          month: `${dayPickerStyles.month} ${dayPickerStyles[clampedSize]}`,
          months: dayPickerStyles.months,
          navBar: '',
          navButtonNext: '',
          navButtonPrev: '',
          navButtonInteractionDisabled: '',
          footer: '',
          todayButton: '',
          week: dayPickerStyles.week,
          weekNumber: '',
          weekday: dayPickerStyles.weekday,
          weekdays: dayPickerStyles.weekdays,
          weekdaysRow: '',
          wrapper: dayPickerStyles.wrapper,

          // Modifiers
          outside: dayPickerStyles.outside,
          selected: dayPickerStyles.selected,
          today: dayPickerStyles.today,
          disabled: dayPickerStyles.disabled,
        }}
        disabledDays={props.disabledDates}
        fixedWeeks={true}
        initialMonth={from}
        modifiers={props.selectionMode === 'range' ? value : undefined}
        modifiersStyles={
          props.selectionMode === 'range'
            ? {
                from: {
                  borderBottomLeftRadius: 10,
                  borderTopLeftRadius: 10,
                },
                to: {
                  borderBottomRightRadius: 10,
                  borderTopRightRadius: 10,
                },
              }
            : undefined
        }
        navbarElement={DateRangePickerNavbar}
        numberOfMonths={isSmall ? 1 : 2}
        onDayClick={(day) => handleDayClick(day)}
        selectedDays={
          props.selectionMode === 'range'
            ? ([from as Date, value as RangeModifier] as any)
            : (from as Date | undefined as any)
        }
      />
      <div className={styles.doneButton} data-zds>
        <LegacyButton isFullWidth={props.size === 'small'} onClick={closeMenu}>
          {props.closeText}
        </LegacyButton>
      </div>
    </div>
  );

  return (
    <div className={styles.root} data-zds>
      <DateRangePickerInput
        {...props}
        clearSelection={clearSelection}
        isOpen={isOpen}
        onClick={() => {
          if (!props.isDisabled && !isOpen) {
            openMenu();
          }
        }}
        renderIconAfter={renderIconAfter}
        renderIconBefore={renderIconBefore}
        options={props.options}
        value={value}
        size={inputSize}
      />
      {isOpen &&
        (variant === 'modal' ? (
          <div
            className={styles.modalBackdrop}
            data-zds
            style={{
              '--_zds-date-range-picker-modal-backdrop-z-index':
                Zindexes.modalOverlay,
            }}
          >
            <div
              className={styles.modal}
              ref={setClickOutsideNode}
              data-size={props.size}
              data-zds
            >
              {menuInner}
            </div>
          </div>
        ) : (
          <FloatingBox
            align={props.menuAlign}
            intersectHeight={460}
            onClickOutside={closeMenu}
            position={props.menuPosition}
          >
            {menuInner}
          </FloatingBox>
        ))}
    </div>
  );
};
